import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiService, graphBuilderApi } from '../../services/api';
import BuildGraphMenu from './BuildGraphMenu';
import NodesTable from '../graph/node/NodesTable';
import SynapsesTable from '../graph/synapses/SynapsesTable';
import GraphEntityExplorer from '../graph/GraphEntityExplorer';
import DeleteConfirmModal from '../common/DeleteConfirmModal';
import RestoreModal from '../common/RestoreModal';
import NodeEntityModal from '../graph/node/NodeEntityModal';
import SynapseEntityModal from '../graph/synapses/SynapseEntityModal';
import GateCreateModal from '../common/GateCreateModal';
import QuickAddNodeModal from '../graph/node/QuickAddNodeModal';
import QuickAddSynapseModal from '../graph/synapses/QuickAddSynapseModal';
import QuickRuleBuilder from '../graph/gates/QuickRuleBuilder';
import BatchRuleBuilder from '../graph/gates/BatchRuleBuilder';
import GateDetailModal from '../graph/gates/GateDetailModal';
import { Pagination } from '../common';
import GraphAgentPanel from '../graph/agent/GraphAgentPanel';
import './BuildGraph.css';
import './ChangeNodesEdges.css';

/**
 * ChangeNodesEdgesPage - Main page for managing graph nodes, synapses, and gates
 * 
 * Features:
 * - Tabbed interface for Nodes, Synapses, and Gates
 * - Advanced search (text and semantic) triggered by button click or Enter key
 * - Bulk selection and delete
 * - Soft delete with undo/restore capability
 * - Orphan node detection
 */
function ChangeNodesEdgesPage() {
  const { graphId } = useParams();
  const navigate = useNavigate();
  
  // Graph state
  const [graph, setGraph] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Tab state
  const [activeTab, setActiveTab] = useState('nodes');
  
  // Search state — searchQuery is the live input value, committedSearchQuery triggers fetches
  const [searchQuery, setSearchQuery] = useState('');
  const [committedSearchQuery, setCommittedSearchQuery] = useState(null);
  const [searchMode, setSearchMode] = useState('text'); // 'text' or 'semantic'
  const [showOrphansOnly, setShowOrphansOnly] = useState(false);
  const [showDeleted, setShowDeleted] = useState(false);
  
  // Graph link summary (process link counts per knowledge node)
  const [nodeLinkSummary, setNodeLinkSummary] = useState(null);

  // Chunk search state
  const [searchChunks, setSearchChunks] = useState(false);
  const [chunkResults, setChunkResults] = useState(null); // { chunks: [...] }
  const [chunkSearchLoading, setChunkSearchLoading] = useState(false);
  const [chunkLinkedElements, setChunkLinkedElements] = useState(null); // { nodes, synapses, gates }
  const [chunkLinkedLoading, setChunkLinkedLoading] = useState(false);
  const [selectedChunkIds, setSelectedChunkIds] = useState(new Set());
  
  // Selection state for bulk operations
  const [selectedNodes, setSelectedNodes] = useState(new Set());
  const [selectedSynapses, setSelectedSynapses] = useState(new Set());
  const [selectedGates, setSelectedGates] = useState(new Set());
  
  // Modal states
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null); // { type: 'node'|'synapse'|'gate', ids: [], names: [] }
  const [showRestoreModal, setShowRestoreModal] = useState(false);
  
  // Graph Entity Explorer state
  const [explorerOpen, setExplorerOpen] = useState(false);
  const [explorerEntity, setExplorerEntity] = useState(null);
  
  // Add/Edit modal states
  const [showNodeModal, setShowNodeModal] = useState(false);
  const [editingNode, setEditingNode] = useState(null); // null = add mode, node object = edit mode
  const [showSynapseModal, setShowSynapseModal] = useState(false);
  const [editingSynapse, setEditingSynapse] = useState(null); // null = add mode, synapse object = edit mode
  const [showAddGateModal, setShowAddGateModal] = useState(false);
  
  // Graph Agent panel state
  const [showAgentPanel, setShowAgentPanel] = useState(false);

  // Quick Add modal states
  const [showQuickAddNode, setShowQuickAddNode] = useState(false);
  const [showQuickAddSynapse, setShowQuickAddSynapse] = useState(false);
  const [showQuickRuleBuilder, setShowQuickRuleBuilder] = useState(false);
  const [showBatchRuleBuilder, setShowBatchRuleBuilder] = useState(false);

  // Gate detail modal state
  const [showGateDetail, setShowGateDetail] = useState(false);
  const [selectedGateId, setSelectedGateId] = useState(null);
  
  // Recompile state
  const [recompiling, setRecompiling] = useState(false);
  const [recompileMessage, setRecompileMessage] = useState(null);
  
  // Refresh trigger
  const [refreshKey, setRefreshKey] = useState(0);
  
  // Gates data
  const [gates, setGates] = useState([]);
  const [gatesLoading, setGatesLoading] = useState(false);
  const [gatesTotalCount, setGatesTotalCount] = useState(0);
  const [gatesPage, setGatesPage] = useState(1);
  const [gatesTotalPages, setGatesTotalPages] = useState(0);
  const [gatesHasSearched, setGatesHasSearched] = useState(false);

  // Search-in-flight tracking for spinner on search button
  const [searchInFlight, setSearchInFlight] = useState(false);

  // Action error banner (replaces alert())
  const [actionError, setActionError] = useState(null);
  const actionErrorTimerRef = useRef(null);

  // Load graph details
  const loadGraphDetails = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiService.getGraphDetails(graphId);
      if (response.success) {
        setGraph(response.graph);
      } else {
        setError('Failed to load graph details');
      }
    } catch (err) {
      console.error('Error loading graph:', err);
      setError(err.response?.data?.message || 'Failed to load graph details');
    } finally {
      setLoading(false);
    }
  }, [graphId]);

  const GATES_PAGE_SIZE = 50;

  // Load gates
  const loadGates = useCallback(async (pageNum = 1) => {
    if (activeTab !== 'gates') return;
    
    try {
      setGatesLoading(true);
      const response = await apiService.listGates({
        graphId,
        includeDeleted: showDeleted,
        page: pageNum,
        pageSize: GATES_PAGE_SIZE,
      });
      if (response.success) {
        setGates(response.gates || []);
        const total = response.totalCount || 0;
        setGatesTotalCount(total);
        setGatesTotalPages(Math.ceil(total / GATES_PAGE_SIZE) || 0);
        setGatesHasSearched(true);
      }
    } catch (err) {
      console.error('Error loading gates:', err);
    } finally {
      setGatesLoading(false);
    }
  }, [graphId, activeTab, showDeleted]);

  useEffect(() => {
    loadGraphDetails();
  }, [loadGraphDetails]);

  // Fetch process link summary for knowledge nodes in this graph
  useEffect(() => {
    if (!graphId) return;
    graphBuilderApi.getGraphLinkSummary({ knowledge_graph_id: graphId })
      .then((summary) => setNodeLinkSummary(summary?.nodes || null))
      .catch(() => setNodeLinkSummary(null));
  }, [graphId]);

  // Only load gates when committedSearchQuery is set (user searched) or on refresh/page change
  useEffect(() => {
    if (activeTab !== 'gates') return;
    if (committedSearchQuery === null && !gatesHasSearched) return;
    loadGates(gatesPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, showDeleted, refreshKey, gatesPage, committedSearchQuery]);

  // Auto-dismiss action error after 6 seconds
  useEffect(() => {
    if (actionError) {
      if (actionErrorTimerRef.current) clearTimeout(actionErrorTimerRef.current);
      actionErrorTimerRef.current = setTimeout(() => setActionError(null), 6000);
    }
    return () => {
      if (actionErrorTimerRef.current) clearTimeout(actionErrorTimerRef.current);
    };
  }, [actionError]);

  const handleBack = () => {
    navigate(`/user-home/graphs/build/${graphId}`);
  };

  // Execute search: commit the current input value so child components fetch
  const handleSearch = () => {
    const trimmed = searchQuery.trim();
    setSearchInFlight(true);
    setGatesPage(1);
    // Use a sentinel to force re-fetch even for same query (e.g. after refresh)
    setCommittedSearchQuery(trimmed);
    // Gates tab: trigger load explicitly
    if (activeTab === 'gates') {
      loadGates(1).finally(() => setSearchInFlight(false));
    } else {
      setTimeout(() => setSearchInFlight(false), 400);
    }
    if (searchChunks && trimmed) {
      handleChunkSearchDirect(trimmed);
    } else if (searchChunks && !trimmed) {
      setChunkResults(null);
      setChunkLinkedElements(null);
    }
  };

  // Clear search: reset both input and committed query
  const handleClearSearch = () => {
    setSearchQuery('');
    setCommittedSearchQuery(null);
    setChunkResults(null);
    setChunkLinkedElements(null);
    setSelectedChunkIds(new Set());
  };

  // Handle Enter key on search input
  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // Handle tab change
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    // Clear selections when switching tabs
    setSelectedNodes(new Set());
    setSelectedSynapses(new Set());
    setSelectedGates(new Set());
  };

  // Get current selection based on active tab
  const getCurrentSelection = () => {
    switch (activeTab) {
      case 'nodes': return selectedNodes;
      case 'synapses': return selectedSynapses;
      case 'gates': return selectedGates;
      default: return new Set();
    }
  };

  // Handle bulk delete
  const handleBulkDelete = () => {
    const selection = getCurrentSelection();
    if (selection.size === 0) return;
    
    setDeleteTarget({
      type: activeTab === 'gates' ? 'gate' : activeTab.slice(0, -1), // Remove 's' for singular
      ids: Array.from(selection),
      isBulk: true,
    });
    setShowDeleteModal(true);
  };

  // Handle single item delete
  const handleSingleDelete = (type, id, name) => {
    setDeleteTarget({
      type,
      ids: [id],
      names: [name],
      isBulk: false,
    });
    setShowDeleteModal(true);
  };

  // Confirm delete
  const confirmDelete = async () => {
    if (!deleteTarget) return;
    
    try {
      const { type, ids, isBulk } = deleteTarget;
      
      if (isBulk) {
        // Bulk delete
        if (type === 'node') {
          await apiService.bulkDeleteNodes(ids);
        } else if (type === 'synapse') {
          await apiService.bulkDeleteSynapses(ids);
        } else if (type === 'gate') {
          // Delete gates one by one (no bulk endpoint yet)
          for (const id of ids) {
            await apiService.deleteGate(id);
          }
        }
      } else {
        // Single delete
        if (type === 'node') {
          await apiService.deleteNode(ids[0]);
        } else if (type === 'synapse') {
          await apiService.deleteSynapse(ids[0]);
        } else if (type === 'gate') {
          await apiService.deleteGate(ids[0]);
        }
      }
      
      // Clear selection and refresh
      setSelectedNodes(new Set());
      setSelectedSynapses(new Set());
      setSelectedGates(new Set());
      setRefreshKey(prev => prev + 1);
      setShowDeleteModal(false);
      setDeleteTarget(null);
    } catch (err) {
      console.error('Delete failed:', err);
      setActionError('Failed to delete: ' + (err.response?.data?.message || err.message));
    }
  };

  // Handle restore
  const handleRestore = async (type, id) => {
    try {
      if (type === 'node') {
        await apiService.restoreNode(id);
      } else if (type === 'synapse') {
        await apiService.restoreSynapse(id);
      } else if (type === 'gate') {
        await apiService.restoreGate(id);
      }
      setRefreshKey(prev => prev + 1);
    } catch (err) {
      console.error('Restore failed:', err);
      setActionError('Failed to restore: ' + (err.response?.data?.message || err.message));
    }
  };

  // Open Graph Entity Explorer for a node (view mode with connections)
  const handleOpenNodeExplorer = (node) => {
    console.log('Opening node explorer for node:', node);
    const nodeId = node.id || node.nodeId || node.NodeId;
    if (!nodeId) {
      console.warn('No nodeId found in node object:', node);
      return;
    }
    
    console.log('Opening explorer with nodeId:', nodeId, 'graphId:', graphId);
    setExplorerEntity({
      type: 'node',
      id: nodeId,
      data: { name: node.name || node.nodeName || node.NodeName }
    });
    setExplorerOpen(true);
  };

  // Open NodeEntityModal for editing a node
  const handleEditNode = (node) => {
    console.log('Opening node modal for editing:', node);
    setEditingNode(node);
    setShowNodeModal(true);
  };

  // Open SynapseEntityModal for editing a synapse
  const handleEditSynapse = (synapse) => {
    console.log('Opening synapse modal for editing:', synapse);
    setEditingSynapse(synapse);
    setShowSynapseModal(true);
  };

  // Open Graph Entity Explorer for a synapse
  const handleOpenSynapseExplorer = (synapse) => {
    const synapseId = synapse.synapseid || synapse.SynapseId || synapse.id;
    if (!synapseId) return;
    
    setExplorerEntity({
      type: 'synapse',
      id: synapseId,
      data: { edgeName: synapse.edgename || synapse.EdgeName || synapse.edgeName }
    });
    setExplorerOpen(true);
  };

  // Open Gate Detail Modal
  const handleOpenGateDetail = (gate) => {
    setSelectedGateId(gate.id);
    setShowGateDetail(true);
  };

  // Handle explorer close
  const handleCloseExplorer = () => {
    setExplorerOpen(false);
    setExplorerEntity(null);
  };

  // Handle entity update from explorer
  const handleEntityUpdated = () => {
    setRefreshKey(prev => prev + 1);
  };

  // Handle chunk search — called directly from handleSearch, not via useEffect
  const handleChunkSearchDirect = async (query) => {
    if (!query.trim() || !searchChunks) {
      setChunkResults(null);
      setChunkLinkedElements(null);
      setSelectedChunkIds(new Set());
      return;
    }
    
    try {
      setChunkSearchLoading(true);
      setChunkLinkedElements(null);
      setSelectedChunkIds(new Set());
      const response = await apiService.searchChunks(graphId, query, searchMode, 20);
      if (response.success) {
        setChunkResults(response);
        // Auto-select all found chunks
        const allIds = new Set((response.chunks || []).map(c => c.chunk_id));
        setSelectedChunkIds(allIds);
        // Auto-fetch linked elements for all found chunks
        if (allIds.size > 0) {
          fetchLinkedElements(Array.from(allIds));
        }
      } else {
        setChunkResults({ chunks: [], total: 0 });
      }
    } catch (err) {
      console.error('Chunk search failed:', err);
      setChunkResults({ chunks: [], total: 0 });
    } finally {
      setChunkSearchLoading(false);
    }
  };

  // Fetch linked elements for selected chunks
  const fetchLinkedElements = async (chunkIds) => {
    if (!chunkIds || chunkIds.length === 0) {
      setChunkLinkedElements(null);
      return;
    }
    
    try {
      setChunkLinkedLoading(true);
      const response = await apiService.getElementsByChunkIds(graphId, chunkIds);
      if (response.success) {
        setChunkLinkedElements(response);
      } else {
        setChunkLinkedElements(null);
      }
    } catch (err) {
      console.error('Failed to fetch linked elements:', err);
      setChunkLinkedElements(null);
    } finally {
      setChunkLinkedLoading(false);
    }
  };

  // Toggle chunk selection and refetch linked elements
  // Side effect is outside the state updater to avoid React anti-pattern
  const handleToggleChunk = (chunkId) => {
    setSelectedChunkIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(chunkId)) {
        newSet.delete(chunkId);
      } else {
        newSet.add(chunkId);
      }
      return newSet;
    });
  };

  // Refetch linked elements whenever selectedChunkIds changes (after toggle)
  useEffect(() => {
    if (selectedChunkIds.size > 0) {
      fetchLinkedElements(Array.from(selectedChunkIds));
    } else if (searchChunks) {
      setChunkLinkedElements(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedChunkIds]);

  // Render linked elements tables (for chunk search results)
  const renderChunkLinkedElements = () => {
    if (chunkLinkedLoading) {
      return <div className="loading-state">Loading linked elements...</div>;
    }
    
    if (!chunkLinkedElements) {
      return null;
    }
    
    const { nodes = [], synapses = [], gates = [] } = chunkLinkedElements;
    
    if (nodes.length === 0 && synapses.length === 0 && gates.length === 0) {
      return (
        <div className="empty-state">
          <p>No elements linked to the selected chunks.</p>
          <p className="empty-hint">This may mean the graph was built before chunk provenance tracking was added. Try reprocessing the document.</p>
        </div>
      );
    }
    
    return (
      <div className="chunk-linked-elements">
        {nodes.length > 0 && (
          <div className="linked-section">
            <h3 className="linked-section-title">Nodes ({nodes.length})</h3>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Type</th>
                  <th>Description</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {nodes.map((node) => (
                  <tr key={node.id}>
                    <td className="node-name-cell">
                      <button className="btn-link" onClick={() => handleOpenNodeExplorer(node)}>
                        {node.name}
                      </button>
                    </td>
                    <td><span className="type-badge">{node.type}</span></td>
                    <td className="description-cell">{node.description?.substring(0, 120)}{node.description?.length > 120 ? '...' : ''}</td>
                    <td className="actions-cell">
                      <button className="btn-link" onClick={() => handleEditNode(node)}>Edit</button>
                      <button className="btn-link delete-link" onClick={() => handleSingleDelete('node', node.id, node.name)}>Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        
        {synapses.length > 0 && (
          <div className="linked-section">
            <h3 className="linked-section-title">Connections ({synapses.length})</h3>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Source</th>
                  <th>Target</th>
                  <th>Description</th>
                  <th>Weight</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {synapses.map((syn) => (
                  <tr key={syn.id}>
                    <td>{syn.sourceNodeId?.substring(0, 12)}...</td>
                    <td>{syn.targetNodeId?.substring(0, 12)}...</td>
                    <td className="description-cell">{syn.description?.substring(0, 120)}{syn.description?.length > 120 ? '...' : ''}</td>
                    <td>{syn.weight != null ? syn.weight.toFixed(3) : '-'}</td>
                    <td className="actions-cell">
                      <button className="btn-link" onClick={() => handleEditSynapse(syn)}>Edit</button>
                      <button className="btn-link delete-link" onClick={() => handleSingleDelete('synapse', syn.id, syn.description)}>Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        
        {gates.length > 0 && (
          <div className="linked-section">
            <h3 className="linked-section-title">Gates ({gates.length})</h3>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Type</th>
                  <th>Description</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {gates.map((gate) => (
                  <tr key={gate.id}>
                    <td>{gate.name}</td>
                    <td><span className="gate-type-badge">{gate.attributes?.gate_type || gate.type}</span></td>
                    <td className="description-cell">{gate.description?.substring(0, 120)}{gate.description?.length > 120 ? '...' : ''}</td>
                    <td className="actions-cell">
                      <button className="btn-link delete-link" onClick={() => handleSingleDelete('gate', gate.id, gate.name)}>Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  // Render gates table
  const renderGatesTable = () => {
    if (gatesLoading) {
      return (
        <div className="gates-table-container">
          <div className="table-loading-overlay">
            <div className="loading-spinner"></div>
            <p>Searching gates...</p>
          </div>
        </div>
      );
    }

    if (!gatesHasSearched) {
      return (
        <div className="empty-state">
          <p>Use the search bar above to find gates.</p>
        </div>
      );
    }

    if (gates.length === 0) {
      return (
        <div className="empty-state">
          <p>No gates found in this graph.</p>
          <p className="empty-hint">Gates are logical nodes (AND, OR, NOT) created during graph building.</p>
        </div>
      );
    }

    return (
      <div className="gates-table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th className="checkbox-cell">
                <input
                  type="checkbox"
                  checked={selectedGates.size === gates.length && gates.length > 0}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedGates(new Set(gates.map(g => g.id)));
                    } else {
                      setSelectedGates(new Set());
                    }
                  }}
                />
              </th>
              <th>Name</th>
              <th>Type</th>
              <th>Inputs</th>
              <th>Outputs</th>
              <th>Description</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {gates.map((gate) => (
              <tr key={gate.id} className={gate.deletedAt ? 'deleted-row' : ''}>
                <td className="checkbox-cell">
                  <input
                    type="checkbox"
                    checked={selectedGates.has(gate.id)}
                    onChange={(e) => {
                      const newSelection = new Set(selectedGates);
                      if (e.target.checked) {
                        newSelection.add(gate.id);
                      } else {
                        newSelection.delete(gate.id);
                      }
                      setSelectedGates(newSelection);
                    }}
                  />
                </td>
                <td>
                  <button
                    className="btn-link"
                    onClick={() => handleOpenGateDetail(gate)}
                  >
                    {gate.name}
                  </button>
                </td>
                <td>
                  <span className={`gate-type-badge gate-type-${gate.gateType?.toLowerCase()}`}>
                    {gate.gateType}
                  </span>
                </td>
                <td>{gate.inputSynapses?.length || 0}</td>
                <td>{gate.outputSynapses?.length || 0}</td>
                <td className="description-cell">{gate.description}</td>
                <td className="actions-cell">
                  <button
                    className="btn-link"
                    onClick={() => handleOpenGateDetail(gate)}
                  >
                    View
                  </button>
                  {gate.deletedAt ? (
                    <button
                      className="btn-link restore-link"
                      onClick={() => handleRestore('gate', gate.id)}
                    >
                      Restore
                    </button>
                  ) : (
                    <button
                      className="btn-link delete-link"
                      onClick={() => handleSingleDelete('gate', gate.id, gate.name)}
                    >
                      Delete
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {gatesTotalPages > 1 && (
          <Pagination
            currentPage={gatesPage}
            totalPages={gatesTotalPages}
            onPageChange={setGatesPage}
            showPageInfo={true}
            className="gates-pagination"
          />
        )}
        {gatesTotalCount > 0 && (
          <div className="table-result-count">{gatesTotalCount} total results</div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="build-graph-page">
        <BuildGraphMenu workspaceId={graph?.workspaceId} />
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading graph...</p>
        </div>
      </div>
    );
  }

  if (error && !graph) {
    return (
      <div className="build-graph-page">
        <BuildGraphMenu workspaceId={graph?.workspaceId} />
        <div className="page-header">
          <h1 className="page-title">Change Nodes/Edges</h1>
        </div>
        <div className="alert alert-error">{error}</div>
        <button className="btn-primary" onClick={handleBack}>
          Back to Build Graph
        </button>
      </div>
    );
  }

  const selectionCount = getCurrentSelection().size;

  return (
    <div className="build-graph-page change-nodes-edges-page">
      <BuildGraphMenu workspaceId={graph?.workspaceId} />
      
      <div className="page-header">
        <div className="header-left">
          <h1 className="page-title">Change Nodes/Edges</h1>
          {graph && <p className="page-subtitle">{graph.graphName}</p>}
        </div>
        <div className="header-right">
          <button
            className={`btn-agent-launch${showAgentPanel ? ' btn-agent-launch--active' : ''}`}
            onClick={() => setShowAgentPanel((v) => !v)}
            title="Launch AI Agent"
          >
            <span className="btn-agent-launch__icon">AI</span>
            {showAgentPanel ? 'Close Agent' : 'Launch AI Agent'}
          </button>
          {activeTab === 'nodes' && (
            <>
              <button className="btn-primary" onClick={() => setShowQuickAddNode(true)}>
                + Quick Add Node
              </button>
              <button className="btn-secondary" onClick={() => {
                setEditingNode(null); // null = add mode
                setShowNodeModal(true);
              }}>
                + Full Add Node
              </button>
            </>
          )}
          {activeTab === 'synapses' && (
            <>
              <button className="btn-primary" onClick={() => setShowQuickAddSynapse(true)}>
                + Quick Add Connection
              </button>
              <button className="btn-secondary" onClick={() => {
                setEditingSynapse(null); // null = add mode
                setShowSynapseModal(true);
              }}>
                + Full Add Connection
              </button>
            </>
          )}
          {activeTab === 'gates' && (
            <>
              <button className="btn-primary" onClick={() => setShowQuickRuleBuilder(true)}>
                + Quick Rule
              </button>
              <button className="btn-secondary" onClick={() => setShowBatchRuleBuilder(true)}>
                Batch Rules
              </button>
              <button className="btn-secondary" onClick={() => setShowAddGateModal(true)}>
                + Full Add Gate
              </button>
            </>
          )}
          <button
            className="btn-secondary"
            disabled={recompiling}
            onClick={async () => {
              if (!window.confirm('Recompile graph Platform artifacts? This rebuilds compiled files without reprocessing documents.')) return;
              setRecompiling(true);
              setRecompileMessage(null);
              try {
                const result = await apiService.recompileGraph(graphId);
                if (result.success) {
                  setRecompileMessage('Graph recompiled successfully');
                } else {
                  setRecompileMessage('Recompile failed: ' + (result.error || 'Unknown error'));
                }
              } catch (err) {
                setRecompileMessage('Recompile failed: ' + (err?.response?.data?.error || err.message));
              } finally {
                setRecompiling(false);
                setTimeout(() => setRecompileMessage(null), 5000);
              }
            }}
          >
            {recompiling ? 'Recompiling...' : 'Recompile Graph'}
          </button>
          <button className="btn-secondary" onClick={handleBack}>
            Back to Build Graph
          </button>
        </div>
      </div>

      {/* Recompile Status Banner */}
      {recompileMessage && (
        <div className={`action-error-banner ${recompileMessage.startsWith('Graph recompiled') ? 'action-success-banner' : ''}`}>
          <span>{recompileMessage}</span>
          <button className="action-error-dismiss" onClick={() => setRecompileMessage(null)}>&times;</button>
        </div>
      )}

      {/* Action Error Banner */}
      {actionError && (
        <div className="action-error-banner">
          <span>{actionError}</span>
          <button className="action-error-dismiss" onClick={() => setActionError(null)}>&times;</button>
        </div>
      )}

      {/* Search and Filter Bar */}
      <div className="search-filter-bar">
        <div className="search-input-group">
          <input
            type="text"
            className="search-input"
            placeholder={`Search ${activeTab}... (press Enter or click Search)`}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={handleSearchKeyDown}
          />
          <button
            className={`btn-search ${searchInFlight ? 'btn-search-loading' : ''}`}
            onClick={handleSearch}
            disabled={searchInFlight}
            title="Execute search"
          >
            {searchInFlight ? (
              <><span className="btn-spinner"></span> Searching...</>
            ) : (
              'Search'
            )}
          </button>
          {committedSearchQuery !== null && (
            <button
              className="btn-clear-search"
              onClick={handleClearSearch}
              title="Clear search"
            >
              Clear
            </button>
          )}
          <div className="search-mode-toggle">
            <button
              className={`mode-btn ${searchMode === 'text' ? 'active' : ''}`}
              onClick={() => setSearchMode('text')}
              title="Text search"
            >
              Text
            </button>
            <button
              className={`mode-btn ${searchMode === 'semantic' ? 'active' : ''}`}
              onClick={() => setSearchMode('semantic')}
              title="AI-powered semantic search"
            >
              Semantic
            </button>
          </div>
        </div>
        
        <div className="filter-options">
          <label className="filter-checkbox chunk-search-checkbox">
            <input
              type="checkbox"
              checked={searchChunks}
              onChange={(e) => setSearchChunks(e.target.checked)}
            />
            <span>Search chunks</span>
          </label>
          {activeTab === 'nodes' && !searchChunks && (
            <label className="filter-checkbox">
              <input
                type="checkbox"
                checked={showOrphansOnly}
                onChange={(e) => setShowOrphansOnly(e.target.checked)}
              />
              <span>Orphan nodes only</span>
            </label>
          )}
          {!searchChunks && (
            <label className="filter-checkbox">
              <input
                type="checkbox"
                checked={showDeleted}
                onChange={(e) => setShowDeleted(e.target.checked)}
              />
              <span>Show deleted</span>
            </label>
          )}
        </div>

        {/* Quick-filter chips */}
        {activeTab === 'nodes' && !searchChunks && (
          <div className="quick-filter-chips">
            <button
              className={`filter-chip ${showOrphansOnly ? 'active' : ''}`}
              onClick={() => setShowOrphansOnly(!showOrphansOnly)}
            >
              Orphans
            </button>
            <button
              className="filter-chip"
              onClick={() => { setSearchQuery('concept'); setCommittedSearchQuery('concept'); }}
            >
              Concepts
            </button>
            <button
              className="filter-chip"
              onClick={() => { setSearchQuery('verbatim_chunk'); setCommittedSearchQuery('verbatim_chunk'); }}
            >
              Verbatim Chunks
            </button>
            <button
              className="filter-chip"
              onClick={() => { setSearchQuery('decision_outcome'); setCommittedSearchQuery('decision_outcome'); }}
            >
              Decisions
            </button>
            <button
              className="filter-chip"
              onClick={() => { setSearchQuery('gate'); setCommittedSearchQuery('gate'); }}
            >
              Gates
            </button>
            <span style={{ borderLeft: '1px solid #d1d5db', height: '20px', margin: '0 4px' }} />
            {['monetary_amount', 'legal_clause', 'policy_provision', 'date_reference', 'organization'].map(dt => (
              <button
                key={dt}
                className="filter-chip"
                onClick={() => { setSearchQuery(dt); setCommittedSearchQuery(dt); }}
                title={`Domain: ${dt.replace(/_/g, ' ')}`}
              >
                {dt.replace(/_/g, ' ')}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Chunk Search Results Panel */}
      {searchChunks && chunkResults && chunkResults.chunks && chunkResults.chunks.length > 0 && (
        <div className="chunk-results-panel">
          <div className="chunk-results-header">
            <span className="chunk-results-title">
              Matching Chunks ({chunkResults.total})
              {chunkResults.mode === 'semantic' && <span className="mode-indicator"> (semantic)</span>}
            </span>
            <button
              className="btn-link"
              onClick={() => {
                // Select/deselect all
                if (selectedChunkIds.size === chunkResults.chunks.length) {
                  setSelectedChunkIds(new Set());
                  setChunkLinkedElements(null);
                } else {
                  const allIds = new Set(chunkResults.chunks.map(c => c.chunk_id));
                  setSelectedChunkIds(allIds);
                  fetchLinkedElements(Array.from(allIds));
                }
              }}
            >
              {selectedChunkIds.size === chunkResults.chunks.length ? 'Deselect All' : 'Select All'}
            </button>
          </div>
          <div className="chunk-results-list">
            {chunkResults.chunks.map((chunk) => (
              <label key={chunk.chunk_id} className={`chunk-result-item ${selectedChunkIds.has(chunk.chunk_id) ? 'selected' : ''}`}>
                <input
                  type="checkbox"
                  checked={selectedChunkIds.has(chunk.chunk_id)}
                  onChange={() => handleToggleChunk(chunk.chunk_id)}
                />
                <div className="chunk-result-info">
                  <span className="chunk-result-title">
                    {chunk.title || `Chunk ${chunk.chunk_index + 1}`}
                    {chunk.score != null && <span className="chunk-score">{(chunk.score * 100).toFixed(0)}% match</span>}
                  </span>
                  <span className="chunk-result-snippet">{chunk.snippet}</span>
                </div>
              </label>
            ))}
          </div>
        </div>
      )}

      {searchChunks && chunkSearchLoading && (
        <div className="chunk-results-panel">
          <div className="loading-state">Searching chunks...</div>
        </div>
      )}

      {searchChunks && chunkResults && chunkResults.chunks && chunkResults.chunks.length === 0 && committedSearchQuery && committedSearchQuery.trim() && !chunkSearchLoading && (
        <div className="chunk-results-panel">
          <div className="empty-state">No chunks match your search.</div>
        </div>
      )}

      {/* Bulk Action Toolbar */}
      {selectionCount > 0 && (
        <div className="bulk-action-toolbar">
          <span className="selection-count">{selectionCount} selected</span>
          <button 
            className="btn-danger"
            onClick={handleBulkDelete}
          >
            Delete Selected
          </button>
          <button 
            className="btn-secondary"
            onClick={() => {
              setSelectedNodes(new Set());
              setSelectedSynapses(new Set());
              setSelectedGates(new Set());
            }}
          >
            Clear Selection
          </button>
        </div>
      )}

      {/* Tabs */}
      <div className="graph-entities-tabs">
        <button
          type="button"
          className={`graph-entities-tab ${activeTab === 'nodes' ? 'is-active' : ''}`}
          onClick={() => handleTabChange('nodes')}
        >
          Nodes
        </button>
        <button
          type="button"
          className={`graph-entities-tab ${activeTab === 'synapses' ? 'is-active' : ''}`}
          onClick={() => handleTabChange('synapses')}
        >
          Connections
        </button>
        <button
          type="button"
          className={`graph-entities-tab ${activeTab === 'gates' ? 'is-active' : ''}`}
          onClick={() => handleTabChange('gates')}
        >
          Gates {gatesTotalCount > 0 && <span className="tab-count">({gatesTotalCount})</span>}
        </button>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {searchChunks && selectedChunkIds.size > 0 ? (
          // Show chunk-linked elements across all entity types
          renderChunkLinkedElements()
        ) : (
          <>
            {activeTab === 'nodes' && (
              <NodesTable
                key={`nodes-${refreshKey}`}
                graphId={graphId}
                searchQuery={searchChunks ? '' : committedSearchQuery}
                searchMode={searchMode}
                showOrphansOnly={showOrphansOnly}
                showDeleted={showDeleted}
                selectedNodes={selectedNodes}
                onSelectionChange={setSelectedNodes}
                onDelete={(id, name) => handleSingleDelete('node', id, name)}
                onRestore={(id) => handleRestore('node', id)}
                onNodeClick={handleOpenNodeExplorer}
                enableSelection={true}
                enableDelete={true}
                requireExplicitSearch={true}
                nodeLinkSummary={nodeLinkSummary}
              />
            )}
            
            {activeTab === 'synapses' && (
              <SynapsesTable
                key={`synapses-${refreshKey}`}
                graphId={graphId}
                searchQuery={searchChunks ? '' : committedSearchQuery}
                showDeleted={showDeleted}
                selectedSynapses={selectedSynapses}
                onSelectionChange={setSelectedSynapses}
                onDelete={(id, desc) => handleSingleDelete('synapse', id, desc)}
                onRestore={(id) => handleRestore('synapse', id)}
                onSynapseClick={handleOpenSynapseExplorer}
                enableSelection={true}
                enableDelete={true}
                requireExplicitSearch={true}
              />
            )}
            
            {activeTab === 'gates' && renderGatesTable()}
          </>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && deleteTarget && (
        <DeleteConfirmModal
          show={showDeleteModal}
          onClose={() => {
            setShowDeleteModal(false);
            setDeleteTarget(null);
          }}
          onConfirm={confirmDelete}
          itemType={deleteTarget.type}
          itemCount={deleteTarget.ids.length}
          itemNames={deleteTarget.names}
          isBulk={deleteTarget.isBulk}
        />
      )}

      {/* Restore Modal */}
      {showRestoreModal && (
        <RestoreModal
          show={showRestoreModal}
          onClose={() => setShowRestoreModal(false)}
          graphId={graphId}
          activeTab={activeTab}
          onRestore={handleRestore}
        />
      )}

      {/* Graph Entity Explorer */}
      <GraphEntityExplorer
        isOpen={explorerOpen}
        initialEntity={explorerEntity}
        graphId={graphId}
        onClose={handleCloseExplorer}
        onEntityUpdated={handleEntityUpdated}
        onEditNode={handleEditNode}
        onEditSynapse={handleEditSynapse}
      />

      {/* Add/Edit Node Modal */}
      <NodeEntityModal
        isOpen={showNodeModal}
        node={editingNode}
        onClose={() => {
          setShowNodeModal(false);
          setEditingNode(null);
        }}
        onEntityUpdated={handleEntityUpdated}
        graphId={graphId}
      />

      {/* Add/Edit Synapse Modal */}
      <SynapseEntityModal
        isOpen={showSynapseModal}
        synapse={editingSynapse}
        onClose={() => {
          setShowSynapseModal(false);
          setEditingSynapse(null);
        }}
        onEntityUpdated={handleEntityUpdated}
        graphId={graphId}
      />

      {/* Add Gate Modal */}
      <GateCreateModal
        isOpen={showAddGateModal}
        onClose={() => setShowAddGateModal(false)}
        onGateCreated={handleEntityUpdated}
        graphId={graphId}
      />

      {/* Quick Add Node Modal */}
      <QuickAddNodeModal
        isOpen={showQuickAddNode}
        onClose={() => setShowQuickAddNode(false)}
        onNodeCreated={handleEntityUpdated}
        graphId={graphId}
      />

      {/* Quick Add Synapse Modal */}
      <QuickAddSynapseModal
        isOpen={showQuickAddSynapse}
        onClose={() => setShowQuickAddSynapse(false)}
        onSynapseCreated={handleEntityUpdated}
        graphId={graphId}
      />

      {/* Quick Rule Builder Modal */}
      <QuickRuleBuilder
        isOpen={showQuickRuleBuilder}
        onClose={() => setShowQuickRuleBuilder(false)}
        onGateCreated={handleEntityUpdated}
        graphId={graphId}
      />

      {/* Batch Rule Builder Modal */}
      <BatchRuleBuilder
        isOpen={showBatchRuleBuilder}
        onClose={() => setShowBatchRuleBuilder(false)}
        onGatesCreated={handleEntityUpdated}
        graphId={graphId}
      />

      {/* Gate Detail Modal */}
      <GateDetailModal
        isOpen={showGateDetail}
        gateId={selectedGateId}
        onClose={() => {
          setShowGateDetail(false);
          setSelectedGateId(null);
        }}
        onNodeClick={(node) => {
          setShowGateDetail(false);
          setSelectedGateId(null);
          handleOpenNodeExplorer(node);
        }}
        onSynapseClick={(synapse) => {
          setShowGateDetail(false);
          setSelectedGateId(null);
          handleOpenSynapseExplorer(synapse);
        }}
      />

      {/* Graph AI Agent Panel */}
      {showAgentPanel && (
        <GraphAgentPanel
          graphId={graphId}
          onClose={() => setShowAgentPanel(false)}
          onGraphChanged={() => setRefreshKey((prev) => prev + 1)}
        />
      )}
    </div>
  );
}

export default ChangeNodesEdgesPage;
